/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View, StyleSheet, TouchableOpacity } from 'react-native';

import Image from '@components/global/Image';
import SociollaText from '@components/sociolla/SociollaText';
import SelectBox from '@components/form/SelectBox';
import Table from '@components/table/Table';
// import TableMobile from '@components/table/TableMobile';
import ListStickyHeader from '@/screens/catalog/components/ListStickyHeader';

import { isWeb, fullWidth } from '@global/screens';
import { parseDate, getTodayMonth } from '@global/helpers';
import { FILTER_PERIOD } from '@/constants';
import Colors from '@global/colors';
import useApi from '@/hooks/useApi';
import { MsCatalog } from '@api/ms-catalog';
import store from '@/store';
import { paramsReseted } from '@/store/params';

import PropTypes from 'prop-types';

const propTypes = {
  navigation: PropTypes.object,
  activeTab: PropTypes.string,
  onChangeTab: PropTypes.func,
  mainBanner: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
};

const defaultProps = {
  navigation: {},
  activeTab: '',
  onChangeTab: () => {},
  mainBanner: false,
};

const tableStructure = {
  image: {
    head: 'Banner',
    body: 'image',
    type: 'image',
    style: {
      width: 120,
      height: 50,
    },
  },
  title: {
    head: 'Promo Title',
    body: 'title',
  },
  promo_date: {
    head: 'Promo Date',
    body: 'promo_date',
  },
  page_type: {
    head: 'Type',
    body: 'page_type',
    type: 'slot',
  },
  status: {
    head: 'Status',
    body: 'is_active',
    type: 'status_active',
  },
};

const Promo = (props) => {
  const { activeTab = '', mainBanner, onChangeTab = () => {} } = props;
  const dispatch = useDispatch();
  const paramsState = useSelector((state) => state.params);

  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [totalData, setTotalData] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const getCMS = useApi(MsCatalog.getCMS);
  const getCMSCount = useApi(MsCatalog.getCMSCount);

  const todayMonth = getTodayMonth();
  const defaultFilter = todayMonth ? todayMonth : FILTER_PERIOD[0];

  const HeaderFilter = useMemo(() => {
    return (
      <View
        style={[
          {
            flexDirection: isWeb ? 'row' : 'column',
          },
          isWeb
            ? {
                zIndex: 1000,
                elevation: 1000,
              }
            : {},
        ]}>
        <View style={[styles.dropDown, !isWeb ? styles.selectMobile : {}]}>
          {!isWeb ? (
            <SociollaText type="bold">Active Period</SociollaText>
          ) : (
            false
          )}
          <View style={styles.filter}>
            <SelectBox
              label="Filter Type"
              hideLabel
              options={FILTER_PERIOD}
              value={selectedPeriod}
              placeholder="Select Filter Type"
              trackBy="value"
              labelKey="label"
              isDropdown={isWeb}
              onChange={(value) => {
                setSelectedPeriod(value);
                if (
                  paramsState?.filter &&
                  Object.keys(paramsState.filter).length
                ) {
                  dispatch(paramsReseted());
                  doSearch();
                }
              }}
            />
          </View>
        </View>
      </View>
    );
  }, [selectedPeriod, paramsState]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setSelectedPeriod(defaultFilter);
      onReset();
      doSearch();
    }
    return () => {
      dispatch(paramsReseted());
      mounted = false;
    };
  }, [props]);

  useEffect(() => {
    let mounted = true;
    setTimeout(() => {
      if (mounted && selectedPeriod?.filter && !loading) {
        onReset();
        doSearch();
      }
    }, 100);
    return () => {
      mounted = true;
    };
  }, [selectedPeriod]);

  const onReset = () => {
    setTotalData(0);
    setTableData([]);
    dispatch(paramsReseted());
  };

  const doSearch = async (newSkip = 0) => {
    const isLoadMore = !isWeb && newSkip > 0;
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const param = JSON.parse(JSON.stringify(store.getState().params));
      const params = {
        params: {
          filter: {},
          skip: param.skip,
          limit: param.limit,
        },
      };
      params.params.filter['is_active'] = true;
      params.params.filter['$or'] = [
        { page_type: 'info' },
        { page_type: 'promo' },
      ];
      if (selectedPeriod?.filter) {
        params.params.filter['$or'][1]['promo_date.from'] =
          selectedPeriod.filter?.from;
        params.params.filter['$or'][1]['promo_date.to'] =
          selectedPeriod.filter?.to;
      } else {
        params.params.filter['$or'][1]['promo_date.from'] =
          defaultFilter.filter?.from;
        params.params.filter['$or'][1]['promo_date.to'] =
          defaultFilter.filter?.to;
      }
      if (params?.params?.skip === 0) {
        const countResponse = await getCMSCount.request(params);
        if (countResponse?.data?.data) {
          setTotalData(countResponse.data.data);
        }
      }
      const response = await getCMS.request(params);
      if (response?.data?.data?.length) {
        const new_response = response.data.data.map((pr) => {
          if (isWeb) {
            pr.image = pr.banner_desktop;
          } else {
            pr.image = pr.banner_mobile;
          }
          if (pr.promo_date) {
            let start = '';
            let end = '';
            if (pr.promo_date?.from) {
              start = parseDate(pr.promo_date.from);
            }
            if (pr.promo_date?.to) {
              end = parseDate(pr.promo_date.to);
            }
            pr.promo_date = `${start} - ${end}`;
          } else {
            pr.promo_date = '-';
          }
          return pr;
        });
        if (isLoadMore) {
          const new_result = tableData.concat(new_response);
          setTableData(new_result);
        } else {
          setTableData(new_response);
        }
      } else {
        setTableData([]);
      }
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    } catch (e) {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
      console.log(e);
    }
  };

  const gotToDetail = (data) => {
    if (data?._id) {
      props.navigation.navigate('CatalogPromoScreen', {
        titleScreen: data.title,
        name: data.title,
        id: data._id,
      });
    }
  };

  return (
    <>
      {isWeb ? (
        <Table
          name="catalog-promo"
          structures={tableStructure}
          data={tableData}
          loading={loading}
          hasCallback
          hasNoHide
          noRowColor
          rowBottomBorder
          showFilter={false}
          showSearch={false}
          totalData={totalData}
          skip={paramsState.skip}
          limit={paramsState.limit}
          searchKey="name"
          placeholder="Search by name"
          onClickRow={({ data }) => gotToDetail(data)}
          onSearch={(value) => doSearch(value)}
          moreInlineFilter={HeaderFilter}
          slot={{
            page_type: ({ data, index }) => {
              return (
                <SociollaText style={{ fontSize: 13 }} key={index}>
                  {data.page_type == 'info' ? 'Info' : 'Promo'}
                </SociollaText>
              );
            },
          }}
        />
      ) : (
        <ListStickyHeader
          name="catalog-promo"
          searchKey="name"
          placeholder="Search by name"
          loading={loading}
          onSearch={(value) => doSearch(value)}
          data={tableData}
          showFilterMobile
          headerComponent={HeaderFilter}
          showSearch={false}
          activeTab={activeTab}
          withLoadMore
          withFetch
          loadingPagination={loadingMore}
          totalData={totalData}
          skip={paramsState.skip}
          limit={paramsState.limit}
          onLoadMore={(newSkip) => doSearch(newSkip)}
          onChangeTab={(val) => onChangeTab(val)}
          mainBanner={mainBanner}
          renderItem={({ data, index }) => (
            <TouchableOpacity
              activeOpacity={0.6}
              style={[styles.productItem, data.style]}
              onPress={() => gotToDetail(data)}
              key={index}>
              <View
                style={{
                  width: fullWidth - 20,
                  flexDirection: 'row',
                }}>
                <View
                  style={{
                    width: 120,
                    height: 50,
                    marginRight: 10,
                    overflow: 'hidden',
                    borderRadius: 5,
                  }}>
                  <Image
                    style={{ width: 120, height: 50 }}
                    width={120}
                    height={50}
                    source={data.image}
                  />
                </View>
                <View
                  style={{
                    width: fullWidth - 150,
                  }}>
                  <SociollaText type="medium" style={{ fontSize: 14 }}>
                    {data.title ? data.title : '-'}
                  </SociollaText>
                  <SociollaText style={{ fontSize: 13, marginVertical: 5 }}>
                    {data.promo_date ? data.promo_date : '-'}
                  </SociollaText>
                </View>
              </View>
            </TouchableOpacity>
          )}
        />
      )}
    </>
  );
};

Promo.propTypes = propTypes;
Promo.defaultProps = defaultProps;

const styles = StyleSheet.create({
  productItem: {
    width: fullWidth,
    paddingVertical: 13,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dropDown: { marginRight: 10, zIndex: 1000, elevation: 1000 },
  selectMobile: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 10,
    marginRight: 0,
  },
  filter: !isWeb ? { width: fullWidth - 130 } : {},
});

export default React.memo(Promo, (prev, next) => prev == next);
